export type StoryUrlComponents =
  | {
      date: Date
      slug: string
      id: string
    }
  | undefined

/**
 * Formats story URL components into a standardized URL path string.
 *
 * @param components - Object containing date, slug, and id for the story URL, or undefined
 * @returns Formatted URL string in the pattern /{year}/{month}/{day}/{slug}/{id}, or '/' if components are undefined
 *
 * @example
 * ```typescript
 * const components = {
 *   date: new Date(2024, 0, 15),
 *   slug: 'my-story',
 *   id: 'abc123'
 * }
 * StoryUrlFormat(components) // Returns: '/2024/01/15/my-story/abc123'
 *
 * StoryUrlFormat(undefined) // Returns: '/'
 * ```
 */
export function StoryUrlFormat(components: StoryUrlComponents): string {
  if (!components) {
    return '/'
  }
  const { date, slug, id }: StoryUrlComponents = components
  const year: number = date.getFullYear()
  const month: string = String(date.getMonth() + 1).padStart(2, '0')
  const day: string = String(date.getDate()).padStart(2, '0')

  return `/${year}/${month}/${day}/${slug}/${id}`
}

/**
 * Parses a story URL string into its component parts.
 *
 * @param url - URL string in the format /{year}/{month}/{day}/{slug}/{id}, with optional leading/trailing slashes
 * @returns Object containing parsed date, slug, and id, or undefined if URL doesn't match expected format
 *
 * @example
 * ```typescript
 * StoryUrlParse('/2024/01/15/my-story/abc123')
 * // Returns: { date: Date(2024-01-15), slug: 'my-story', id: 'abc123' }
 *
 * StoryUrlParse('2024/01/15/my-story/abc123/')
 * // Returns: { date: Date(2024-01-15), slug: 'my-story', id: 'abc123' }
 *
 * StoryUrlParse('/invalid/url')
 * // Returns: undefined
 * ```
 */
export function StoryUrlParse(url: string): StoryUrlComponents {
  const regex: RegExp = /^\/?(\d{4})\/(\d{2})\/(\d{2})\/([^/]+)\/([^/]+)\/?$/
  const match: RegExpMatchArray | null = url.match(regex)

  if (!match) {
    return undefined
  }

  const [, year, month, day, slug, id]: RegExpMatchArray = match
  const date: Date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day))

  return {
    date,
    slug,
    id
  }
}
